import { supabase } from '../lib/supabase';

export interface DocumentoVerificado {
  id: string;
  numero: string;
  titulo: string;
  modulo_codigo: string;
  status: string;
  data_abertura: string;
  data_conclusao: string | null;
  hash_publico: string;
  numero_publicado: string | null;
  tipo_ato: string | null;
  tipo_pl: string | null;
  ementa: string | null;
  data_publicacao: string | null;
  autoridade_nome: string | null;
  autoridade_cargo: string | null;
  pdf_url: string | null;
}

export interface AssinaturaVerificada {
  id: string;
  tipo: 'simples' | 'avancada' | 'qualificada';
  signatario_nome: string;
  signatario_cpf: string | null;     // mascarado: 123.***.***-45
  assinado_em: string;
  hash_conteudo: string | null;
  hash_algoritmo: string | null;
  certificado_emissor: string | null;
  certificado_serial: string | null;
  certificado_validade: string | null;
  carimbo_tempo: string | null;
  carimbo_servidor: string | null;
  simulada: boolean;
  fundamento_legal: string | null;
}

export interface ResumoVerificacao {
  total: number;
  qualificadas: number;
  avancadas: number;
  simples: number;
  simuladas: number;
  todas_simuladas: boolean;
}

export interface ResultadoVerificacao {
  verificado: boolean;
  erro?: string;
  documento?: DocumentoVerificado;
  estabelecimento?: { nome: string; cor_primaria: string | null };
  assinaturas?: AssinaturaVerificada[];
  resumo?: ResumoVerificacao;
  verificado_em?: string;
  /** Integridade: true = hash confere; false = não confere (alterado); null = não informado. */
  integridade_ok?: boolean | null;
  /** Modo mínimo (verificação por código): só autenticidade + integridade, sem conteúdo. */
  minimo?: boolean;
}

export const verificacaoService = {
  /** Verificação por código opaco (todo documento assinado). Modo mínimo (sem conteúdo sigiloso). */
  async verificarPorCodigo(codigo: string, hash?: string): Promise<ResultadoVerificacao> {
    const { data, error } = await supabase.rpc('documento_verificar_por_codigo', {
      p_codigo: codigo,
      p_hash: hash ?? null,
    });
    if (error) throw error;
    const r = data as ResultadoVerificacao;
    if (r?.verificado) r.minimo = true;
    return r;
  },

  /** Verificação pública por número (atos/DOM já publicados). Mantida p/ compatibilidade. */
  async verificar(codigo: string, hash?: string): Promise<ResultadoVerificacao> {
    const { data, error } = await supabase.rpc('documento_verificar_publico', {
      p_numero: codigo,
      p_hash: hash ?? null,
    });
    if (error) throw error;
    return data as ResultadoVerificacao;
  },

  /**
   * Resolve o código: tenta primeiro por código opaco; se não achar, tenta como
   * número de documento público (compat com QRs de atos/DOM gerados antes).
   */
  async resolver(codigo: string, hash?: string): Promise<ResultadoVerificacao> {
    const porCodigo = await this.verificarPorCodigo(codigo, hash);
    if (porCodigo.verificado) return porCodigo;
    const porNumero = await this.verificar(codigo, hash);
    if (porNumero.verificado) return porNumero;
    // Devolve o erro mais informativo (o por-código, que é o caminho principal).
    return porCodigo;
  },
};
